import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from 'ui/inputFieldset';
import { Label } from "../../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import { Badge } from "../../ui/badge";
import {
  Plus,
  Edit2,
  Search,
  DollarSign,
  Check,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Star,
  X,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  CurrencyApi,
  getCurrencies,
  createCurrency,
  updateCurrency,
} from "../../services/currencyMasterService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface Currency {
  id: string;
  currencyCode: string;
  currencyName: string;
  currencySymbol: string;
  exchangeRateToBase: number;
  lastUpdated: string;
  isBaseCurrency: boolean;
  isActive: boolean;
}

export const CurrencyMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [filterBase, setFilterBase] = useState<"all" | "base" | "nonbase">(
    "all"
  );

  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [formData, setFormData] = useState<Partial<Currency>>({
    currencyCode: "",
    currencyName: "",
    currencySymbol: "",
    exchangeRateToBase: 1,
    isBaseCurrency: false,
    isActive: true,
  });

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const { canAdd, canEdit, screenName, icon: ScreenIcon } = useScreenPermission();

  const baseCurrency = currencies.find((c) => c.isBaseCurrency);

  const [errors, setErrors] = useState<Partial<Record<keyof Currency, string>>>(
    {}
  );

  const mapApiToUI = (api: CurrencyApi): Currency => ({
    id: String(api.currencyId),
    currencyCode: api.currencyCode,
    currencyName: api.currencyName,
    currencySymbol: api.currencySymbol,
    exchangeRateToBase: api.exchangeRate,
    lastUpdated: api.updatedAt ?? api.createdAt,
    isBaseCurrency: api.isBaseCurrency,
    isActive: api.isActive,
  });

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setLoading(true);
        const res = await getCurrencies({
          searchText: searchTerm || null,
          currencyName: null,
          currencyCode: null,
          isBaseCurrency: filterBase === "all" ? null : filterBase === "base",
          isActive: filterStatus === "all" ? null : filterStatus === "active",
          pageNo: currentPage,
          pageSize,
        });

        setCurrencies(res.items.map(mapApiToUI));
        setTotalCount(res.totalCount);
      } catch {
        toast.error("Failed to load currencies");
      } finally {
        setLoading(false);
      }
    };

    loadCurrencies();
  }, [searchTerm, filterStatus, filterBase, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterBase]);

  const reloadCurrencies = async () => {
    try {
      setLoading(true);
      const res = await getCurrencies({
        searchText: searchTerm || null,
        currencyName: null,
        currencyCode: null,
        isBaseCurrency: filterBase === "all" ? null : filterBase === "base",
        isActive: filterStatus === "all" ? null : filterStatus === "active",
        pageNo: currentPage,
        pageSize,
      });

      setCurrencies(res.items.map(mapApiToUI));
      setTotalCount(res.totalCount);
      } finally {
        setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingId(null);
    setErrors({});
    setFormData({
      currencyCode: "",
      currencyName: "",
      currencySymbol: "",
      exchangeRateToBase: 1,
      isBaseCurrency: false,
      isActive: true,
    });
  };

  const handleSave = async () => {
    const newErrors: Partial<Record<keyof Currency, string>> = {};

    if (!formData.currencyCode) {
      newErrors.currencyCode = "Currency code is required";
    } else if (formData.currencyCode.length !== 3) {
      newErrors.currencyCode = "Currency code must be 3 characters";
    }

    if (!formData.currencyName) {
      newErrors.currencyName = "Currency name is required";
    }

    if (!formData.currencySymbol) {
      newErrors.currencySymbol = "Currency symbol is required";
    }

    if (!formData.exchangeRateToBase || formData.exchangeRateToBase <= 0) {
      newErrors.exchangeRateToBase = "Exchange rate must be greater than 0";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const duplicateCode = currencies.find(
      (c) =>
        c.currencyCode.toLowerCase() === formData.currencyCode?.toLowerCase() &&
        c.id !== editingId
    );

    const duplicateName = currencies.find(
      (c) =>
        c.currencyName.toLowerCase() === formData.currencyName?.toLowerCase() &&
        c.id !== editingId
    );

    if (duplicateCode) {
      newErrors.currencyCode = "Currency code already exists";
    }

    if (duplicateName) {
      newErrors.currencyName = "Currency name already exists";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const {
      currencyCode,
      currencyName,
      currencySymbol,
      exchangeRateToBase,
      isBaseCurrency = false,
      isActive = true,
    } = formData as Required<
      Pick<
        Currency,
        | "currencyCode"
        | "currencyName"
        | "currencySymbol"
        | "exchangeRateToBase"
        | "isBaseCurrency"
        | "isActive"
      >
    >;

    try {
      setLoading(true);
      if (editingId) {
        await updateCurrency(Number(editingId), {
          currencyId: Number(editingId),
          currencyCode,
          currencyName,
          currencySymbol,
          exchangeRate: exchangeRateToBase,
          isBaseCurrency,
          isActive,
          updatedBy: 1,
        });

        toast.success("Currency updated successfully");
      } else {
        await createCurrency({
          currencyCode,
          currencyName,
          currencySymbol,
          exchangeRate: exchangeRateToBase,
          isBaseCurrency,
          isActive,
          createdBy: 1,
        });

        toast.success("Currency added successfully");
      }

      await reloadCurrencies();
      handleCloseDialog();
    } catch {
      toast.error("Failed to save currency");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (currency: Currency) => {
    setEditingId(currency.id);
    setFormData(currency);
    setShowDialog(true);
  };

  const clearFilters = () => {
    setFilterStatus("all");
    setFilterBase("all");
    setSearchTerm("");
    toast.success("Filters cleared");
  };

  const displayedCurrencies = currencies;

  const sortedCurrencies = displayedCurrencies.sort((a, b) => {
    if (sortColumn) {
      const aValue = a[sortColumn as keyof Currency];
      const bValue = b[sortColumn as keyof Currency];
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
    }
    return 0;
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const SortableHeader = ({
    column,
    label,
  }: {
    column: string;
    label: string;
  }) => (
    <TableHead
      style={{ color: "#5B9BD5" }}
      className="px-4 py-3 text-xs cursor-pointer hover:bg-[#5B9BD5]/10 transition-colors select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
        {sortColumn === column ? (
          sortDirection === "asc" ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </div>
    </TableHead>
  );

  const activeFiltersCount =
    (filterStatus !== "all" ? 1 : 0) + (filterBase !== "all" ? 1 : 0);

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search by currency code, name, or symbol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant={showAdvancedFilter ? "default" : "outline"}
              size="icon"
              onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
              style={showAdvancedFilter ? { background: "#5B9BD5" } : {}}
              className={
                showAdvancedFilter
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }
              title="Toggle Advanced Filters"
            >
              <Filter className="w-5 h-5" />
            </Button>
            {canAdd && (
              <Button
                onClick={() => setShowDialog(true)}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Currency
              </Button>
            )}
          </div>
        </div>

        {/* Base Currency Card */}
        {baseCurrency && (
          <Card className="border-[#5B9BD5] bg-gradient-to-r from-sky-50 to-blue-50 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#5B9BD5] flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 sm:w-7 sm:h-7 text-white fill-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs px-2 py-1">
                      Base Currency
                    </Badge>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2 truncate">
                    {baseCurrency.currencySymbol} {baseCurrency.currencyCode}
                  </h2>
                  <p className="text-gray-600 text-sm sm:text-base truncate">
                    {baseCurrency.currencyName}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Currencies</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {currencies.length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Active</p>
                    <p className="text-2xl font-bold text-green-900">
                      {currencies.filter((c) => c.isActive).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Base Currency</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {currencies.find((c) => c.isBaseCurrency)?.currencyCode ||
                        "-"}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Star className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">Inactive</p>
                    <p className="text-2xl font-bold text-amber-900">
                      {currencies.filter((c) => !c.isActive).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters Section */}
        {showAdvancedFilter && (
          <Card
            className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter currencies by multiple criteria
                  </CardDescription>
                </div>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Filter */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Status
                  </Label>
                  <Select
                    value={filterStatus}
                    onValueChange={(value: "all" | "active" | "inactive") =>
                      setFilterStatus(value)
                    }
                  >
                    <SelectTrigger className="text-sm h-9">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Currency Type Filter */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Currency Type
                  </Label>
                  <Select
                    value={filterBase}
                    onValueChange={(value: "all" | "base" | "nonbase") =>
                      setFilterBase(value)
                    }
                  >
                    <SelectTrigger className="text-sm h-9">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="base">Base Currency</SelectItem>
                      <SelectItem value="nonbase">Non-Base Currency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary */}
              {activeFiltersCount > 0 && (
                <div className="pt-3 border-t">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-gray-600 font-medium">
                      Active filters:
                    </span>
                    {filterStatus !== "all" && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-white border-green-300 text-green-700"
                      >
                        Status:{" "}
                        <span className="font-semibold ml-1">{filterStatus}</span>
                        <button
                          onClick={() => setFilterStatus("all")}
                          className="ml-1.5 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    )}
                    {filterBase !== "all" && (
                      <Badge
                        variant="outline"
                        className="text-xs bg-white border-purple-300 text-purple-700"
                      >
                        Type:{" "}
                        <span className="font-semibold ml-1">
                          {filterBase === "base" ? "Base Currency" : "Non-Base"}
                        </span>
                        <button
                          onClick={() => setFilterBase("all")}
                          className="ml-1.5 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Showing{" "}
                    <span className="font-semibold text-[#5B9BD5]">
                      {displayedCurrencies.length}
                    </span>{" "}
                    of <span className="font-semibold">{currencies.length}</span>{" "}
                    currencies
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
          <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50" style={{ gap: "0px", padding: "8px 24px" }}>
            <CardDescription>
              {displayedCurrencies.length} currenc
              {displayedCurrencies.length !== 1 ? "ies" : "y"} found
              {baseCurrency && (
                <> • Exchange rates relative to {baseCurrency.currencyCode}</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <SortableHeader column="currencyCode" label="Code" />
                    <SortableHeader column="currencyName" label="Name" />
                    <SortableHeader column="currencySymbol" label="Symbol" />
                    <SortableHeader
                      column="exchangeRateToBase"
                      label={`Rate (to ${baseCurrency?.currencyCode || "Base"})`}
                    />
                    <SortableHeader column="lastUpdated" label="Last Updated" />
                    <SortableHeader column="isActive" label="Status" />
                    {canEdit && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedCurrencies.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No currencies found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCurrencies.map((currency) => (
                      <TableRow key={currency.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-[10px] px-1.5 py-0.5"
                            >
                              {currency.currencyCode}
                            </Badge>
                            {currency.isBaseCurrency && (
                              <Star className="w-3.5 h-3.5 text-purple-600 fill-purple-600" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-900 text-sm">
                            {currency.currencyName}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-gray-900 text-sm font-semibold">
                            {currency.currencySymbol}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700 font-mono">
                            {currency.isBaseCurrency
                              ? "1.0000"
                              : currency.exchangeRateToBase.toFixed(4)}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-600">
                            {currency.lastUpdated}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            className={`${currency.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {currency.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        {canEdit && (
                          <TableCell className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(currency)}
                                className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                title="Edit currency"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                              {/* <Button
                                size="sm"
                                variant="ghost"
                                // onClick={() => handleDelete(currency.id)}
                                className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                title="Delete currency"
                                disabled={currency.isBaseCurrency}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button> */}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {displayedCurrencies.length === 0 ? (
                <div className="text-center py-8 px-4 text-gray-500 text-sm">
                  No currencies found
                </div>
              ) : (
                sortedCurrencies.map((currency) => (
                  <div
                    key={currency.id}
                    className="p-4 hover:bg-sky-50/30 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] font-mono text-xs px-2 py-0.5"
                          >
                            {currency.currencyCode}
                          </Badge>
                          <span className="text-lg font-semibold text-gray-900">
                            {currency.currencySymbol}
                          </span>
                          {currency.isBaseCurrency && (
                            <Star className="w-4 h-4 text-purple-600 fill-purple-600" />
                          )}
                        </div>
                        <h3 className="font-medium text-gray-900">
                          {currency.currencyName}
                        </h3>
                      </div>
                      <Badge
                        className={`${currency.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                      >
                        {currency.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>

                    <div className="space-y-2 mb-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Rate to {baseCurrency?.currencyCode}:
                        </span>
                        <span className="font-mono text-gray-900">
                          {currency.isBaseCurrency
                            ? "1.0000"
                            : currency.exchangeRateToBase.toFixed(4)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Updated:</span>
                        <span className="text-gray-900">
                          {currency.lastUpdated}
                        </span>
                      </div>
                      {currency.isBaseCurrency && (
                        <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-[10px] px-1.5 py-0.5">
                          Base Currency
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(currency)}
                        className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                      >
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                      {/* <Button
                        size="sm"
                        variant="outline"
                      //   onClick={() => handleDelete(currency.id)}
                        className="flex-1 text-red-600 border-red-300 hover:bg-red-50 h-8"
                        disabled={currency.isBaseCurrency}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Delete
                      </Button> */}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            label="currencies"
          />
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {editingId ? "Edit Currency" : "Add Currency"}
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update currency details and exchange rate"
                  : "Add a new currency with exchange rate"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Currency Code"
                  required
                  id="currencyCode"
                  placeholder="e.g., USD, EUR, INR"
                  value={formData.currencyCode}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      currencyCode: e.target.value.toUpperCase(),
                    });
                    setErrors({ ...errors, currencyCode: undefined });
                  }}
                  maxLength={3}
                  className="text-sm uppercase"
                />

                {errors.currencyCode && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.currencyCode}
                  </p>
                )}

                <p className="text-xs text-gray-500">3-letter ISO 4217 code</p>
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Currency Name"
                  required
                  id="currencyName"
                  placeholder="e.g., US Dollar"
                  value={formData.currencyName}
                  onChange={(e) => {
                    setFormData({ ...formData, currencyName: e.target.value });
                    setErrors({ ...errors, currencyName: undefined });
                  }}
                  className="text-sm"
                />

                {errors.currencyName && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.currencyName}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Currency Symbol"
                  required
                  id="currencySymbol"
                  placeholder="e.g., $, €, ₹"
                  value={formData.currencySymbol}
                  onChange={(e) => {
                    setFormData({ ...formData, currencySymbol: e.target.value });
                    setErrors({ ...errors, currencySymbol: undefined });
                  }}
                  className="text-sm"
                />

                {errors.currencySymbol && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.currencySymbol}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Exchange Rate (to USD)"
                  id="exchangeRate"
                  type="number"
                  step="0.0001"
                  placeholder="e.g., 83.25"
                  value={formData.exchangeRateToBase}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      exchangeRateToBase: Number(e.target.value),
                    });
                    setErrors({ ...errors, exchangeRateToBase: undefined });
                  }}
                  className="text-sm"
                  disabled={formData.isBaseCurrency}
                />

                {errors.exchangeRateToBase && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.exchangeRateToBase}
                  </p>
                )}

                <p className="text-xs text-gray-500">
                  1 {baseCurrency?.currencyCode || "Base"} ={" "}
                  {formData.exchangeRateToBase} {formData.currencyCode || "XXX"}
                </p>
              </div>

              {/* <div className="flex items-center justify-between py-2 px-3 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <Label
                    htmlFor="isBaseCurrency"
                    className="text-sm cursor-pointer"
                  >
                    Set as Base Currency
                  </Label>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Only one currency can be base
                  </p>
                </div>
                <Switch
                  id="isBaseCurrency"
                  checked={formData.isBaseCurrency}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      isBaseCurrency: checked,
                      exchangeRateToBase: checked
                        ? 1
                        : formData.exchangeRateToBase,
                    })
                  }
                />
              </div> */}

              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                <Label htmlFor="isActive" className="text-sm cursor-pointer">
                  Active Status
                </Label>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCloseDialog}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm"
              >
                {editingId ? "Update" : "Add"} Currency
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
